import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Alert, Button, Card, Collapse, Drawer, Modal, Space, Spin, Tooltip, Typography,
} from 'antd';
import request from '../../../../../../logic/utils/request';
import getLinks from '../../../../../../logic/utils/getLinks';
import ParamRow from './ParamRow';
import { PARAM_GROUPS, getParamMeta, isValidJson } from './paramsMeta';

const toValue = (value) => (value === null || value === undefined ? '' : String(value));

const BINDING_URL = '/server/api/officeparamsbinding/';

function PlaceParams({
  params: isOpen, setParams: close, placeId, placeName, callNumbers, defaultParams,
}) {
  const [bindings, setBindings] = useState([]);
  const [draft, setDraft] = useState({});
  const [soundData, setSoundData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const write = getLinks(true);

  const fetchBindings = async () => {
    const res = await request(BINDING_URL, 'get', { wp_id: placeId });
    return res.data.data;
  };

  // Действующее значение: переопределение места, иначе базовое
  const savedValues = useMemo(() => Object.fromEntries(defaultParams.map((param) => {
    const binding = bindings.find((item) => item.officeparam_id === param.id);
    return [param.id, toValue(binding ? binding.overridevalue : param.value)];
  })), [defaultParams, bindings]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let active = true;
    setLoading(true);
    Promise.all([fetchBindings(), axios.get('/api/soundlist')])
      .then(([list, sound]) => {
        if (!active) return;
        setBindings(list);
        setSoundData([sound.data.sounds, sound.data.variables]);
        setDraft(Object.fromEntries(defaultParams.map((param) => {
          const binding = list.find((item) => item.officeparam_id === param.id);
          return [param.id, toValue(binding ? binding.overridevalue : param.value)];
        })));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isOpen, placeId]);

  const items = defaultParams.map((param) => ({ param, meta: getParamMeta(param) }));
  const dirty = items.filter(({ param }) => draft[param.id] !== savedValues[param.id]);
  const invalid = items.filter(({ param, meta }) => meta.control === 'json' && !isValidJson(draft[param.id]));

  const isHidden = ({ meta }) => {
    if (!meta.dependsOn) return false;
    const master = defaultParams.find((param) => param.param === meta.dependsOn);
    return master && draft[master.id] === '0';
  };

  const save = async () => {
    setSaving(true);
    try {
      const toAdd = [];
      const ops = [];
      dirty.forEach(({ param }) => {
        const value = draft[param.id];
        const binding = bindings.find((item) => item.officeparam_id === param.id);
        const isDefault = value === toValue(param.value);
        if (binding) {
          ops.push(isDefault
            ? request(`${BINDING_URL}${binding.id}/`, 'delete')
            : request(`${BINDING_URL}${binding.id}/`, 'update', { overridevalue: value }));
        } else if (!isDefault) {
          toAdd.push({ officeparam_id: param.id, overridevalue: value });
        }
      });
      await Promise.all(ops);
      if (toAdd.length) {
        // Как и раньше: add получает полный набор переопределений места
        const current = await fetchBindings();
        await request(BINDING_URL, 'add', {
          wp_id: placeId,
          params: [
            ...toAdd,
            ...current.map(({ officeparam_id: id, overridevalue }) => ({ officeparam_id: id, overridevalue })),
          ],
        });
      }
      toast.success('Параметры сохранены');
      close();
    } catch (e) {
      // Часть изменений могла примениться — сверяемся с сервером, черновик не трогаем
      toast.error('Не удалось сохранить все параметры');
      fetchBindings().then(setBindings).catch(() => {});
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!dirty.length) {
      close();
      return;
    }
    Modal.confirm({
      title: 'Закрыть без сохранения?',
      content: `Несохранённых изменений: ${dirty.length}`,
      okText: 'Закрыть',
      cancelText: 'Остаться',
      onOk: close,
    });
  };

  const renderRows = (groupItems) => groupItems.filter((item) => !isHidden(item)).map(({ param, meta }, index) => (
    <ParamRow
      key={param.id}
      first={index === 0}
      meta={meta}
      value={draft[param.id] ?? ''}
      defaultValue={toValue(param.value)}
      disabled={!write}
      soundData={soundData}
      callNumbers={callNumbers}
      onChange={(value) => setDraft((prev) => ({ ...prev, [param.id]: value }))}
    />
  ));

  const groups = PARAM_GROUPS
    .map((group) => ({ ...group, items: items.filter(({ meta }) => meta.group === group.key) }))
    .filter((group) => group.items.length);

  let saveHint = '';
  if (invalid.length) saveHint = 'Исправьте некорректный JSON';
  else if (!dirty.length) saveHint = 'Нет изменений';

  const footer = (
    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
      <Typography.Text type="secondary">
        {dirty.length ? `Несохранённых изменений: ${dirty.length}` : ''}
      </Typography.Text>
      <Space>
        <Button onClick={handleClose}>Отмена</Button>
        {write && (
          <Tooltip title={saveHint}>
            <Button type="primary" loading={saving} disabled={Boolean(saveHint)} onClick={save}>
              Сохранить
            </Button>
          </Tooltip>
        )}
      </Space>
    </Space>
  );

  return (
    <Drawer
      title={placeName ? `Параметры · ${placeName}` : 'Параметры рабочего места'}
      open={isOpen}
      onClose={handleClose}
      width={680}
      destroyOnClose
      footer={footer}
    >
      {loading ? <Spin style={{ display: 'block', marginTop: 48 }} /> : (
        <>
          <Alert
            type="info"
            style={{ marginBottom: 8 }}
            message="По умолчанию действуют общие настройки. Изменённые здесь значения применяются только к этому рабочему месту."
          />
          {groups.map((group) => (group.collapsed ? (
            <Collapse
              key={group.key}
              style={{ marginTop: 16 }}
              defaultActiveKey={group.items.some(({ param }) => draft[param.id] !== toValue(param.value)) ? [group.key] : []}
            >
              <Collapse.Panel header={<b>{group.title}</b>} key={group.key}>
                {renderRows(group.items)}
              </Collapse.Panel>
            </Collapse>
          ) : (
            <Card
              key={group.key}
              size="small"
              title={group.title}
              style={{ marginTop: 16 }}
              headStyle={{ background: '#fafafa' }}
              bodyStyle={{ padding: '0 16px' }}
            >
              {renderRows(group.items)}
            </Card>
          )))}
        </>
      )}
    </Drawer>
  );
}

export default React.memo(PlaceParams);
